import { useEffect, useState } from 'react'
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { Send } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

const ticketPath = '/ticket-management/all-tickets'

export type TicketUpdateItem = {
  id: number
  ticket_request_id: number
  user_id: number | null
  content: string
  type: string
  is_internal: boolean
  author?: {
    id: number
    first_name?: string | null
    last_name?: string | null
    user_login?: string | null
    user_email?: string | null
    full_name: string
  } | null
  created_at: string
  updated_at: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  ticketId: string
  ticketNumber?: string
  onSubmitted?: () => void
}

export default function TicketUpdateModal({ isOpen, onClose, ticketId, ticketNumber, onSubmitted }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [content, setContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setContent('')
    }
  }, [isOpen])

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed) return

    setSubmitting(true)
    try {
      await apiClient.post(`${ticketPath}/${ticketId}/updates`, {
        content: trimmed,
        type: 'comment',
        is_internal: false,
      })
      showSuccess('Success', 'Comment added')
      setContent('')
      onSubmitted?.()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent>
        <ModalHeader pb={2}>
          <Text fontWeight="700" color={colors.heading}>
            Add comment to ticket
          </Text>
          <Text fontSize="xs" color={colors.textMuted} fontWeight="400" mt={0.5}>
            {ticketNumber ? `${ticketNumber} — Add a comment for this ticket request.` : `Ticket #${ticketId} — Add a comment for this ticket request.`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={5}>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color={colors.text}>
                Comment
              </FormLabel>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your comment here... (Ctrl+Enter to submit)"
                size="sm"
                rows={3}
                resize="vertical"
                borderColor={colors.border}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
            </FormControl>
            <HStack justify="flex-end">
              <Button
                leftIcon={<Send size={14} />}
                colorScheme="blue"
                size="sm"
                onClick={handleSubmit}
                isLoading={submitting}
                isDisabled={!content.trim()}
              >
                Add comment
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
